import { useMemo, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { useShareGate } from "@/hooks/useShareGate";

import { buildCitation, fullUrl, plain, toHtml, localToday, type Fields, type Seg, type Style, type SourceType } from "./citationFormat";

const TOOL_INFO_CONTENT = {
  what: `Generates properly formatted citations (with italics where each style requires them, and the right date format for each) in APA, MLA, Chicago, or Harvard style for websites, books, and journal articles, and builds an alphabetized reference list as you add sources.`,
  how: `Choose your citation style and source type, fill in the source details, and the formatted citation appears instantly. Add it to your running reference list and copy the full list when you're done.`,
  tip: `Double-check author name formatting and capitalization against the original source before finalizing — citation generators handle the structural formatting reliably, but the accuracy of the underlying details is still on the person entering them.`,
};

export default function CitationGenerator() {
  const [style, setStyle] = useState<Style>("APA");
  const [type, setType] = useState<SourceType>("Website");
  const [fields, setFields] = useState<Fields>({
    authorLast: "Smith",
    authorFirst: "Jane",
    extraAuthors: [],
    title: "Understanding Climate Change",
    year: "2025",
    pubDate: "",
    siteName: "National Geographic",
    publisher: "Academic Press",
    city: "New York",
    url: "https://example.com/article",
    accessDate: "",
    journalName: "Journal of Environmental Science",
    volume: "12",
    issue: "3",
    pages: "45-60",
  });
  const [savedList, setSavedList] = useState<Seg[][]>([]);

  const update = (field: keyof Fields, value: string) =>
    setFields(f => ({ ...f, [field]: value }));

  const updateExtra = (i: number, k: "last" | "first", value: string) =>
    setFields(f => ({ ...f, extraAuthors: (f.extraAuthors ?? []).map((a, n) => (n === i ? { ...a, [k]: value } : a)) }));
  const urlNote = type === "Website" && fields.url.trim() && fullUrl(fields.url) !== fields.url.trim() ? `Shown as ${fullUrl(fields.url)} (https:// added).` : "";

  const { gateShare } = useShareGate();

  const citation = useMemo(
    () => buildCitation(style, type, fields),
    [style, type, fields]
  );

  // Copies rich text (italics survive pasting into Word/Google Docs) with a plain-text fallback.
  const copyRich = (list: Seg[][], okMsg: string) => gateShare(async () => {
    const html = list.map((c) => `<p style="margin-left:0.5in;text-indent:-0.5in">${toHtml(c)}</p>`).join("");
    const text = list.map(plain).join("\n\n");
    try {
      if (typeof ClipboardItem !== "undefined" && navigator.clipboard.write) {
        await navigator.clipboard.write([new ClipboardItem({ "text/html": new Blob([html], { type: "text/html" }), "text/plain": new Blob([text], { type: "text/plain" }) })]);
      } else await navigator.clipboard.writeText(text);
      toast.success(okMsg);
      return true;
    } catch {
      try { await navigator.clipboard.writeText(text); toast.success(okMsg + " (plain text)"); return true; }
      catch { toast.error("Couldn't copy. Select the text and copy it by hand."); return false; }
    }
  });
  const copy = () => copyRich([citation], "Citation copied (italics included)");
  const addToList = () => { setSavedList([...savedList, citation]); toast.success("Added to your reference list"); };
  const sortKey = (c: Seg[]) => plain(c).replace(/^["'“]/, "").toLowerCase();
  const sortedList = useMemo(() => [...savedList].sort((a, b) => sortKey(a).localeCompare(sortKey(b))), [savedList]);
  const copyAll = () => copyRich(sortedList, "Full reference list copied (alphabetized)");

  const { blocked } = useLogToolUsage(true, 3000, { ownActions: true });


  return (
    <ToolContainer>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <Label className="mb-1 block text-sm">Citation Style</Label>
          <Select value={style} onValueChange={v => setStyle(v as Style)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="APA">APA (7th edition)</SelectItem>
              <SelectItem value="MLA">MLA (9th edition)</SelectItem>
              <SelectItem value="Chicago">
                Chicago (17th edition, Notes-Bibliography)
              </SelectItem>
              <SelectItem value="Harvard">Harvard</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1 block text-sm">Source Type</Label>
          <Select value={type} onValueChange={v => setType(v as SourceType)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Website">Website</SelectItem>
              <SelectItem value="Book">Book</SelectItem>
              <SelectItem value="Journal Article">Journal Article</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <div>
          <Label htmlFor="cg-last-0" className="mb-1 block text-sm">Author Last Name</Label>
          <Input id="cg-last-0" value={fields.authorLast} onChange={e => update("authorLast", e.target.value)} />
        </div>
        <div>
          <Label htmlFor="cg-first-0" className="mb-1 block text-sm">Author First Name</Label>
          <Input id="cg-first-0" value={fields.authorFirst} onChange={e => update("authorFirst", e.target.value)} />
        </div>
        {(fields.extraAuthors ?? []).map((a, i) => (
          <div key={i} className="md:col-span-2 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
            <div>
              <Label htmlFor={`cg-last-${i + 1}`} className="mb-1 block text-sm">Author {i + 2} Last Name</Label>
              <Input id={`cg-last-${i + 1}`} value={a.last} onChange={e => updateExtra(i, "last", e.target.value)} />
            </div>
            <div>
              <Label htmlFor={`cg-first-${i + 1}`} className="mb-1 block text-sm">Author {i + 2} First Name</Label>
              <Input id={`cg-first-${i + 1}`} value={a.first} onChange={e => updateExtra(i, "first", e.target.value)} />
            </div>
            <Button type="button" variant="outline" size="sm" aria-label={`Remove author ${i + 2}`} onClick={() => setFields(f => ({ ...f, extraAuthors: (f.extraAuthors ?? []).filter((_, k) => k !== i) }))}>Remove</Button>
          </div>
        ))}
        <div className="md:col-span-2 flex gap-2 flex-wrap">
          <Button type="button" variant="outline" size="sm" onClick={() => setFields(f => ({ ...f, extraAuthors: [...(f.extraAuthors ?? []), { last: "", first: "" }] }))}>Add author</Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => setFields(f => ({ ...f, authorLast: "", authorFirst: "", extraAuthors: [], title: "", year: "", pubDate: "", siteName: "", publisher: "", city: "", url: "", journalName: "", volume: "", issue: "", pages: "" }))}>Clear all fields</Button>
        </div>
        <div className="md:col-span-2">
          <Label className="mb-1 block text-sm">Title</Label>
          <Input aria-label="Title"
            value={fields.title}
            onChange={e => update("title", e.target.value)}
          />
        </div>
        <div>
          <Label className="mb-1 block text-sm">Year (blank = n.d.)</Label>
          <Input aria-label="Year (blank = n.d.)"
            value={fields.year}
            onChange={e => update("year", e.target.value)}
          />
        </div>

        {type === "Website" && (
          <>
            <div>
              <Label className="mb-1 block text-sm">Site Name</Label>
              <Input aria-label="Site Name"
                value={fields.siteName}
                onChange={e => update("siteName", e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <Label className="mb-1 block text-sm">URL</Label>
              <Input aria-label="URL"
                value={fields.url}
                onChange={e => update("url", e.target.value)}
              />
              {urlNote && <p className="text-xs text-muted-foreground mt-1">{urlNote}</p>}
            </div>
            <div>
              <Label className="mb-1 block text-sm">
                Access Date (blank = today)
              </Label>
              <Input
                type="date"
                value={fields.accessDate}
                max={localToday()}
                onChange={e => update("accessDate", e.target.value)}
              />
            </div>
            <div>
              <Label className="mb-1 block text-sm">Full publication date (optional)</Label>
              <Input aria-label="Full publication date (optional)" type="date" value={fields.pubDate} onChange={e => update("pubDate", e.target.value)} />
            </div>
          </>
        )}

        {type === "Book" && (
          <>
            <div>
              <Label className="mb-1 block text-sm">Publisher</Label>
              <Input aria-label="Publisher"
                value={fields.publisher}
                onChange={e => update("publisher", e.target.value)}
              />
            </div>
            <div>
              <Label className="mb-1 block text-sm">
                City (Chicago/Harvard only)
              </Label>
              <Input
                value={fields.city}
                onChange={e => update("city", e.target.value)}
              />
            </div>
          </>
        )}

        {type === "Journal Article" && (
          <>
            <div className="md:col-span-2">
              <Label className="mb-1 block text-sm">Journal Name</Label>
              <Input aria-label="Journal Name"
                value={fields.journalName}
                onChange={e => update("journalName", e.target.value)}
              />
            </div>
            <div>
              <Label className="mb-1 block text-sm">Volume</Label>
              <Input aria-label="Volume"
                value={fields.volume}
                onChange={e => update("volume", e.target.value)}
              />
            </div>
            <div>
              <Label className="mb-1 block text-sm">Issue</Label>
              <Input aria-label="Issue"
                value={fields.issue}
                onChange={e => update("issue", e.target.value)}
              />
            </div>
            <div>
              <Label className="mb-1 block text-sm">Pages</Label>
              <Input aria-label="Pages"
                value={fields.pages}
                onChange={e => update("pages", e.target.value)}
              />
            </div>
          </>
        )}
      </div>

      <Card className="p-4 mb-3">
        <p className="text-xs text-muted-foreground mb-1">{style} citation</p>
        <p
          className="text-sm"
          style={{ textIndent: "-1.5em", marginLeft: "1.5em" }}
        >
          {blocked ? "— blocked at today's usage limit —" : citation.map((sg, k) => (sg.i ? <i key={k}>{sg.t}</i> : <span key={k}>{sg.t}</span>))}
        </p>
      </Card>

      <div className="flex gap-3">
        <Button onClick={copy} disabled={blocked}>Copy Citation</Button>
        <Button variant="outline" onClick={addToList} disabled={blocked}>
          Add to Reference List
        </Button>
      </div>

      {savedList.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium">
              Your Reference List ({savedList.length})
            </p>
            <Button size="sm" variant="outline" onClick={copyAll}>
              Copy Full List
            </Button>
          </div>
          <div className="space-y-2">
            {sortedList.map((c, i) => (
              <p key={i} className="text-sm p-2 rounded bg-muted/30" style={{ textIndent: "-1.5em", marginLeft: "1.5em" }}>
                {c.map((sg, k) => (sg.i ? <i key={k}>{sg.t}</i> : <span key={k}>{sg.t}</span>))}
              </p>
            ))}
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
